import { copyText } from '../copied'
import '../plan.css'
import { api, postNow } from '../api'
import { routeGen } from '../route-state'
import { worksAlone } from '../me'
import { clear, el, loadError, shortDate } from '../ui'
import { BANDS, OVER_TOP_BAND, bandRangeWords, poundsWords, type Band } from '../../functions/lib/plan'
import { FREE_MONTHS, FREE_OFFER_WORDS, type FreeOffer } from '../../functions/lib/free-time'

// Your plan (billing stage 2, docs/plans/billing/PLAN.md): the owner's active customers, the price band they put the
// round in, the free months and what counts, from GET /api/business/plan. More > Your business, owners of a UK round
// with free months only, and never inside the iPhone app (Apple 3.1.3: no prices or paying there; main.ts sends it
// back to the Diary). Stage 3: once billing is switched on, Add a card (Stripe's page, the first payment the morning after
// the last free day) and, with a card, Change card or cancel (Stripe's Customer Portal).
interface PlanRow {
  activeCustomers: number
  countedOn: string
  band: { key: string; name: string; from: number; to: number | null; pence: number }
  freeOffer: FreeOffer
  freeFrom: string | null
  freeUntil: string | null
  onTrial: boolean
  referral?: { link: string; joined: number; earned: number }
  billing?: { on: boolean; live: boolean; card: string | null; amountPence: number | null; firstPaymentOn: string | null; nextPaymentOn: string | null; endsOn: string | null }
  // Stage 6: what the round may do, and the oldest bill still unpaid with Stripe's page to pay it.
  access?: { access: 'open' | 'paused' | 'view_only'; why: 'free_time_ended' | 'unpaid' | 'plan_ended' | null; since: string | null; stopped?: boolean }
  unpaid?: { pence: number; dueOn: string | null; payLink: string | null; needsAction: boolean } | null
  // Stage 8b: a partner free year given to this round, its last free day.
  partnerYearUntil?: string | null
}

const TERMS_URL = 'https://roundly.work/terms'
const dayAfter = (iso: string): string => { const t = new Date(`${iso}T00:00:00Z`); t.setUTCDate(t.getUTCDate() + 1); return t.toISOString().slice(0, 10) }

// "22 March 2027": a day on its own, never moved by the phone's clock.
const fullDate = (iso: string | null): string =>
  iso ? new Date(`${iso}T00:00:00Z`).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric', timeZone: 'UTC' }) : ''

const COUNTS = [
  'Regular and ad hoc customers alike',
  'Visits brought in from another app or a spreadsheet, if done in the last 12 months',
  'Jobs marked done while your round is paused',
  'Customers you have stopped or deleted, until their last done visit is 12 months old',
]

export function mountPlan(): void {
  const gen = routeGen
  const root = document.querySelector<HTMLElement>('#screen')!
  clear(root)
  // A one-person round has no workers to include (Liam, 19 Oct 2026).
  root.append(el('div', { class: 'page-heading' }, el('div', {}, el('h2', {}, 'Your plan'), el('p', { class: 'muted' }, `One monthly price for your whole round, set by how many active customers it has.${worksAlone() ? '' : ' Every worker is included.'}`))))
  const body = el('div', { class: 'plan-page' }, el('p', { class: 'muted' }, 'Counting your customers…'))
  root.append(body)
  api<PlanRow>('/api/business/plan', 'business-plan')
    .then((p) => { if (gen === routeGen) draw(body, p) })
    .catch((err: Error) => { if (gen === routeGen) { body.replaceChildren(); loadError(body, err.message, () => mountPlan(), undefined, 'Your plan') } })
}

// Your card (billing stage 3), only once billing is switched on: Add a card with the terms ticked, or the card, when the
// first or next payment is, and Change card or cancel.
function cardSection(p: PlanRow, band: Band): HTMLElement | null {
  const b = p.billing
  if (!b?.on) return null
  // A missed payment owed and no plan running (stopped at 30 days): it is paid first, from Payment due above, never a
  // new plan beside it.
  if (!b.live && p.unpaid) return null
  const section = el('section', { class: 'plan-section plan-card' }, el('h3', {}, 'Your card'))
  const err = el('p', { class: 'form-error', role: 'alert' })
  const go = (button: HTMLElement, path: string, body: unknown): void => {
    button.setAttribute('disabled', 'disabled')
    err.textContent = ''
    postNow<{ url: string }>(path, body)
      .then((r) => { window.location.href = r.url })
      .catch((e: Error) => { button.removeAttribute('disabled'); err.textContent = e.message || "That didn't open. Try again." })
  }
  if (b.live) {
    // What the plan itself will take (never today's count: a band change has its own notice, stage 5).
    const amount = b.amountPence ? `: ${poundsWords(b.amountPence)}` : ''
    section.append(el('p', { class: 'plan-card-line' }, b.card ?? 'Card on file'))
    if (b.endsOn) section.append(el('p', { class: 'muted' }, `Your plan ends on ${fullDate(b.endsOn)}. Nothing more is taken.`))
    else if (b.firstPaymentOn) section.append(el('p', { class: 'muted' }, `First payment on ${fullDate(b.firstPaymentOn)}${amount}. Nothing is taken before then.`))
    else if (b.nextPaymentOn) section.append(el('p', { class: 'muted' }, `Next payment on ${fullDate(b.nextPaymentOn)}${amount}.`))
    const change = el('button', { type: 'button', class: 'btn-secondary' }, 'Change card or cancel')
    change.addEventListener('click', () => go(change, '/api/billing/portal', {}))
    section.append(el('div', { class: 'plan-card-acts' }, change), err)
    return section
  }
  const freeLeft = p.onTrial && p.freeUntil && p.freeUntil >= p.countedOn
  const when = freeLeft && p.freeUntil ? `Nothing is taken until ${fullDate(dayAfter(p.freeUntil))}, and you can cancel any time before then.` : 'Your first payment is taken when you add your card.'
  const tick = el('input', { type: 'checkbox', id: 'plan-terms' }) as HTMLInputElement
  const label = el('label', { for: 'plan-terms', class: 'plan-terms' }, tick, ' I agree to ', el('a', { href: TERMS_URL, target: '_blank', rel: 'noopener' }, "Roundly's terms"))
  const add = el('button', { type: 'button', class: 'btn-primary' }, 'Add a card')
  add.addEventListener('click', () => {
    if (!tick.checked) { err.textContent = "Tick that you agree to Roundly's terms first."; return }
    go(add, '/api/billing/checkout', { consent: true })
  })
  section.append(el('p', {}, `${poundsWords(band.pence)} a month for your round. ${when}`), label, el('div', { class: 'plan-card-acts' }, add), err,
    el('p', { class: 'muted plan-small' }, 'Card payments are handled by Stripe. Roundly never sees or keeps your full card number.'))
  return section
}

// A payment still unpaid (stage 6): how much, since when, what happens next, and Stripe's page to pay it. Free months
// ended without a card: said above the card.
function dueSection(p: PlanRow): HTMLElement | null {
  const a = p.access?.access ?? 'open'
  const u = p.unpaid
  if (!u) {
    return a === 'paused' && p.access?.why === 'free_time_ended'
      ? el('p', { class: 'plan-note plan-note-warn', role: 'status' }, 'Your free months have ended, so your round is paused. Add a card below to open everything again.')
      : a === 'paused' && p.access?.why === 'plan_ended'
        ? el('p', { class: 'plan-note plan-note-warn', role: 'status' }, 'Your plan has ended, so your round is paused. Add a card below to start it again.')
        : null
  }
  const from = u.dueOn ? ` from ${fullDate(u.dueOn)}` : ''
  const what = a === 'view_only' && p.access?.stopped ? 'Your round is view and download only and your plan has stopped. Paying it opens everything again, and your monthly payments start again from that day.'
    : a === 'view_only' ? 'Your round is view and download only until it is paid.'
    : a === 'paused' ? 'Your round is paused until it is paid.'
    : 'If it is still unpaid 14 days after it was due, your round is paused.'
  const section = el('section', { class: 'plan-section plan-due' }, el('h3', {}, 'Payment due'),
    el('p', {}, `Your ${poundsWords(u.pence)} payment${from} hasn't gone through.${u.needsAction ? ' Your bank wants you to confirm it.' : ''} ${what}`))
  if (u.payLink) {
    // Stripe's own page for this bill (the server only ever gives invoice.stripe.com).
    const pay = el('button', { type: 'button', class: 'btn-primary' }, u.needsAction ? 'Confirm with your bank' : 'Pay now')
    const link = u.payLink
    pay.addEventListener('click', () => { window.location.href = link })
    section.append(el('div', { class: 'plan-card-acts' }, pay))
  }
  // A plan still running can have its card changed below; a stopped one is paid on Stripe's page, with any card.
  if (p.billing?.live) section.append(el('p', { class: 'muted plan-small' }, 'To use a different card, tap Change card or cancel below.'))
  return section
}

function referralSection(r: { link: string; joined: number; earned: number }): HTMLElement {
  const box = el('input', { type: 'text', class: 'plan-ref-link', id: 'plan-ref-link', readonly: 'readonly', value: r.link, 'aria-label': 'Your referral link' }) as HTMLInputElement
  const copy = el('button', { type: 'button', class: 'btn-secondary' }, 'Copy link')
  copy.addEventListener('click', () => {
    void copyText(r.link, { button: copy, what: 'link' }).then(copied => { if (!copied) box.select() })
  })
  const so = r.joined === 0 ? 'Nobody has signed up through it yet.'
    : `${r.joined} ${r.joined === 1 ? 'business has' : 'businesses have'} signed up through it${r.earned ? `, and you've earned £45 off ${r.earned} ${r.earned === 1 ? 'time' : 'times'}` : ''}.`
  return el('section', { class: 'plan-section plan-ref' }, el('h3', {}, 'Refer another business'),
    el('p', {}, "Send another window cleaner or round your link. They get 4 months free instead of 3. Once they've paid for 3 months, you get £45 off your Roundly bills."),
    el('div', { class: 'plan-ref-row' }, box, copy),
    el('p', { class: 'muted' }, `${so} Credit comes off future bills and has no cash value.`))
}

function draw(body: HTMLElement, p: PlanRow): void {
  const n = p.activeCustomers
  const ladder = [...BANDS, OVER_TOP_BAND]
  const yours = ladder.find((b) => b.key === p.band.key) ?? BANDS[0]
  const next = ladder[ladder.indexOf(yours) + 1] as Band | undefined

  // The count, the band and its price, the way the price list shows them.
  const count = el(
    'section',
    { class: 'plan-count' },
    el('span', { class: 'plan-count-label' }, 'Active customers'),
    el('strong', { class: 'plan-count-value' }, n.toLocaleString('en-GB')),
    el('span', { class: 'plan-count-note' }, `Counted today, ${shortDate(p.countedOn)}. A customer counts if they've had a visit marked done in the last 12 months.`),
  )
  const price = el(
    'section',
    { class: 'plan-band' },
    el('span', { class: 'plan-count-label' }, 'Your price'),
    el('strong', { class: 'plan-band-value' }, `${poundsWords(yours.pence)} a month`),
    el('span', { class: 'plan-count-note' }, yours.key === 'agreed'
      ? `Over 1,500 active customers: we agree a price with you. Until then it's ${poundsWords(yours.pence)} a month.`
      : `${yours.name}: ${bandRangeWords(yours)} active customers.${next ? ` From ${next.from.toLocaleString('en-GB')} it's ${next.key === 'agreed' ? 'a price we agree with you' : `${next.name}, ${poundsWords(next.pence)}`}, from your next payment at least a week after we tell you.` : ''}`),
  )
  body.replaceChildren(el('div', { class: 'plan-top' }, count, price))
  // Back from Stripe's page: the card is saved there at once and reaches here a moment later.
  if (/[?&]card=saved/.test(window.location.hash)) {
    body.prepend(el('p', { class: 'plan-note', role: 'status' }, 'Card saved. It can take a minute to show here.'))
    history.replaceState(null, '', `${window.location.pathname}#/plan`)
  }
  const due = dueSection(p)
  if (due) body.append(due)
  const card = cardSection(p, yours)
  if (card) body.append(card)

  // Every band, yours marked.
  const table = el('table', { class: 'plan-bands' })
  table.append(el('thead', {}, el('tr', {}, el('th', {}, 'Active customers'), el('th', {}, 'Price a month'))))
  const tbody = el('tbody', {})
  for (const b of BANDS) {
    const row = el('tr', b.key === yours.key ? { class: 'plan-bands-yours', 'aria-current': 'true' } : {},
      el('td', {}, bandRangeWords(b), b.key === yours.key ? el('span', { class: 'plan-bands-tag' }, 'Yours') : ''),
      el('td', {}, poundsWords(b.pence)))
    tbody.append(row)
  }
  tbody.append(el('tr', yours.key === 'agreed' ? { class: 'plan-bands-yours', 'aria-current': 'true' } : {}, el('td', {}, 'Over 1,500'), el('td', {}, 'We agree a price with you')))
  table.append(tbody)
  body.append(el('section', { class: 'plan-section' }, el('h3', {}, 'The price list'), el('div', { class: 'plan-table-wrap' }, table),
    el('p', { class: 'muted' }, "When your round moves into another band, we email you when we count it, and the new price starts from your next monthly payment that's at least a week after that email. It never changes part-way through a month you've paid for. Roundly isn't VAT registered, so no VAT is added.")))

  // The free months.
  if (p.onTrial && p.freeUntil) {
    const months = FREE_MONTHS[p.freeOffer]
    const offer = p.freeOffer === 'standard' ? `${months} months free` : `${months} months free (${FREE_OFFER_WORDS[p.freeOffer]})`
    // The ask is only promised while they last and no card has been added (stage 6).
    const ended = p.freeUntil < p.countedOn
    const section = el('section', { class: 'plan-section plan-free' }, el('h3', {}, 'Your free months'),
      p.partnerYearUntil
        ? el('p', {}, `${offer} from ${fullDate(p.freeFrom)}, and your partner free year, ${ended ? 'to' : 'until'} ${fullDate(p.freeUntil)}.${ended ? ' They have ended.' : ''}`)
        : el('p', {}, `${offer}, from ${fullDate(p.freeFrom)} ${ended ? 'to' : 'until'} ${fullDate(p.freeUntil)}.${ended ? ' They have ended.' : ''}`))
    if (!ended && !p.billing?.live) section.append(el('p', { class: 'muted' }, "Before they end we'll ask whether you want to carry on. Nothing is charged unless you say yes and add a card."))
    body.append(section)
  }

  // Refer another business (billing stage 7): the round's own link to copy, and what it has brought.
  if (p.referral) body.append(referralSection(p.referral))

  // What counts.
  const list = el('ul', { class: 'plan-list' })
  for (const line of COUNTS) list.append(el('li', {}, line))
  body.append(el('section', { class: 'plan-section' }, el('h3', {}, 'What counts as an active customer'), list,
    el('p', { class: 'muted' }, "A visit you undo isn't counted. We count the day before each monthly payment.")))
}
